import {withSection} from './quoteTemplateSchema';
import type {PlateValue, QuoteFont, QuoteSection, QuoteSectionType, QuoteTemplate} from '../resolver/types';

export type QuoteContentBlock = {
	id: string;
	sectionType: QuoteSectionType;
	name: string;
	/** The Plate AST as JSON, merge tokens unresolved. */
	bodyJson: string;
	isDefault: boolean;
};

export type QuoteBrandingDefault = {accentColor: string | null; font: QuoteFont | null} | null;

export type SourceBlockState = 'none' | 'missing' | 'dirty' | 'clean';

/**
 * Parses a block body, returning null rather than throwing on a payload we cannot read. Each call
 * parses afresh, so nothing a section ends up holding is shared with the block list.
 */
export function parseBlockBody(block: QuoteContentBlock): PlateValue | null {
	try {
		const parsed = JSON.parse(block.bodyJson);
		return Array.isArray(parsed) ? (parsed as PlateValue) : null;
	} catch {
		return null;
	}
}

/**
 * What the saved-block dropdown should show for a section.
 *
 * `sourceBlockId` is provenance, never a source of truth: the section body is always
 * authoritative, so this never resolves content THROUGH the id. Resolving through it would mean
 * editing a block silently rewrote every quote that ever used it.
 */
export function sourceBlockState(section: QuoteSection, blocks: readonly QuoteContentBlock[]): SourceBlockState {
	if (!section.sourceBlockId) return 'none';
	const block = blocks.find(candidate => candidate.id === section.sourceBlockId);
	if (!block) return 'missing';
	const body = parseBlockBody(block);
	// A block we cannot parse cannot be compared against; reporting the section as diverged is
	// honest, reporting it as clean would not be.
	if (body === null) return 'dirty';
	return JSON.stringify(body) === JSON.stringify(section.body) ? 'clean' : 'dirty';
}

/** Replaces a section's copy with a saved block's, recording where it came from. */
export function applyContentBlock(template: QuoteTemplate, block: QuoteContentBlock): QuoteTemplate {
	const body = parseBlockBody(block);
	if (body === null) return template;
	return withSection(template, block.sectionType, {body, sourceBlockId: block.id});
}
